import { useDashboardUIActions } from "@contexts/dashboardUI";
import { useProjectActions } from "@contexts/projects";
import { checkIfCubBased, checkLabel, formatNumberToCurrency, formatNumberToLocale } from "@utils/formatNumber";
import { itemIndexedBudgetDisplay } from "@utils/indexedBudget";
import { useProjectContext } from "@contexts/projects";
import { isBuildingTrial } from "@utils/checkPermiossion";
import { useAuthContext } from "@contexts/auth";
import {
  AMPLI_SERIES_LABELS,
  filterCashFlowColumns,
  isAmpliCurveVisible,
  physicalColumnLabel,
} from "../cashFlowPlanning";
import { metricPresentation, metricReasonStyle } from "@utils/metricPresentation";
import { monthlySeriesTotalOrNull } from "../ampliSeries";
import AnnulmentIndicator from "@components/titleAnnulments/AnnulmentIndicator";


const defaultRow = (label, value, compare, indexedToToday = false, conversion = {}, indicator = null) => {
  const available = typeof value === "number" && Number.isFinite(value);
  const indexerAvailable = !conversion.cubBased || (
    typeof conversion.indexerValue === "number"
    && Number.isFinite(conversion.indexerValue)
    && conversion.indexerValue !== 0
  );
  const status = !available
    ? "source_unavailable"
    : !indexerAvailable ? "no_basis" : "ok";
  const presented = metricPresentation({
    id: "projection_value",
    value: status === "ok" ? checkIfCubBased(value, indexedToToday) : null,
    status,
  }, {
    formatter: formatNumberToLocale,
    reasonId: conversion.reasonId,
  });
  return <div className="data" style={{ width: '170px' }}>
    <span className="label">{label}</span>
    <span className={`value ${(available && value < 0) ? `${label.includes("Estouros / Economias") ? 'color-green' : 'color-red'}` : ''}`}>
      <span aria-describedby={presented.describedBy} data-status={presented.status}>{presented.text}</span>
      {presented.reason && (
        <span id={presented.describedBy} style={metricReasonStyle}>{presented.reason}</span>
      )}
      {indicator}
    </span>
  </div>
}

const { useState, useCallback, Fragment } = require("react")

const AccordionItem = ({ item, type, typeTitle, ChildAccordionComponent }) => {
  const { toggleItemDetailOffcanvas } = useDashboardUIActions();
  const { selectDetail } = useProjectActions();
  const { activeProject, activeProject: { wbs_code_id }, filterDocs, selectedGraph, advancedItems, cubBased } = useProjectContext();
  const { user: { configurations: { projection: { columns } } } } = useAuthContext();
  const visibleColumns = filterCashFlowColumns(columns, activeProject.cashFlowPlanning);
  const ampliCurveVisible = isAmpliCurveVisible(activeProject);
  const reasonKey = String(item?.wbs_code_id ?? item?.building_unit_id ?? "item")
    .replace(/[^a-zA-Z0-9_-]/g, "-");
  const renderDefaultRow = (slot, label, value, compare, indexedToToday = false, indicator = null) => defaultRow(
    label,
    value,
    compare,
    indexedToToday,
    {
      cubBased,
      indexerValue: indexedToToday
        ? activeProject?.indexer?.value
        : activeProject?.originalIndexer?.value,
      reasonId: `metric-financial-projection-${reasonKey}-${slot}-reason`,
    },
    indicator,
  );
  // Título anulado apropriado neste item (ou em ancestral/descendente): ícone ao lado de Pago, A pagar e Comprometido.
  const annulled = (kind) => (
    <AnnulmentIndicator summary={activeProject.titleAnnulments} kind={kind} wbsCode={item?.wbs_code_id ?? ""} />
  );

  const [toggle, setToggle] = useState(false)
  const onShowDetail = useCallback(() => {
    selectDetail(item, type)
    toggleItemDetailOffcanvas()
  }, [item, type])

  const wbsType = `wbs_code_id_${type}`

  const getLevel = () => {
    const shadesOfGray = ['#f8f8f8', '#f0f0f0', '#e8e8e8', '#e0e0e0', '#d8d8d8'];
    const marginsLeft = ['0px', '12px', '24px', '36px', '48px']
    let res;
    let mar;
    switch (type) {
      case 'unit':
        res = shadesOfGray[0]
        mar = marginsLeft[0]
        break;

      case 'cell':
        res = shadesOfGray[1]
        mar = marginsLeft[1]
        break;

      case 'step':
        res = shadesOfGray[2]
        mar = marginsLeft[2]
        break;

      case 'substep':
        res = shadesOfGray[3]
        mar = marginsLeft[3]
        break;

      case 'service':
        res = shadesOfGray[4]
        mar = marginsLeft[4]
        break;

      default:
        break;
    }
    return { backgroundColor: res, marginLeft: mar }
  }

  const wbsFlat = advancedItems?.map(i => i.wbs_code_id)

  const getAdvBorder = () => {
    if (selectedGraph !== 'advancementGraph') return ''
    if (wbsFlat.some(i => i?.includes(item.wbs_code_id))) {
      if (wbsFlat.some(i => i === item.wbs_code_id)) {
        return { backgroundColor: '#cce7c9' }
      }
      return { borderColor: '#a1ca9c', zIndex: 1 }
    } else {
      return ''
    }
  }

  const getStyles = () => {
    const level = getLevel()
    const adv = getAdvBorder()
    return { display: 'flex', ...level, ...adv }
  }

  const commit = ((item.filteredToPay - filterDocs.toPayData.flatMap(i => i.itens).filter(i => i[wbsType] === item.wbs_code_id).reduce((a, b) => a + b.value, 0)) + (item.filteredPaid - filterDocs.paidData.flatMap(i => i.itens).filter(i => i[wbsType] === item.wbs_code_id).reduce((a, b) => a + b.value, 0)))

  const checkCard = (i) => {
    switch (i) {
      case 'physical':
        return renderDefaultRow(
          i,
          checkLabel(physicalColumnLabel(activeProject)),
          item.filteredPhysicals,
        )
      case 'disbursement':
        return renderDefaultRow(
          i,
          checkLabel(activeProject.cashFlowPlanning?.state === 'needs_adjustment'
            ? 'Desembolso (em ajuste)'
            : activeProject.cashFlowPlanning?.effectiveSource === 'disbursement'
              ? 'Previsão de caixa (Desembolso)'
              : 'Desembolso'),
          item.filteredDisbursement,
        )
      case 'ampliDisbursement':
        return renderDefaultRow(
          i,
          checkLabel(AMPLI_SERIES_LABELS.ampliDisbursement),
          ampliCurveVisible ? monthlySeriesTotalOrNull(item.monthlyProjection?.ampliDisbursement) : null,
        )
      case 'cashProjection':
        return renderDefaultRow(
          i,
          checkLabel(AMPLI_SERIES_LABELS.cashProjection),
          ampliCurveVisible ? monthlySeriesTotalOrNull(item.monthlyProjection?.cashProjection) : null,
        )
      case 'paid':
        return renderDefaultRow(i, checkLabel('Pago'), (item.filteredPaid - filterDocs.paidData.flatMap(i => i.itens).filter(i => i[wbsType] === item.wbs_code_id).reduce((a, b) => a + b.value, 0)), undefined, false, annulled('paid'))
      case 'toPay':
        return renderDefaultRow(i, checkLabel('A pagar'), (item.filteredToPay - filterDocs.toPayData.flatMap(i => i.itens).filter(i => i[wbsType] === item.wbs_code_id).reduce((a, b) => a + b.value, 0)), undefined, false, annulled('toPay'))
      case 'paidToPay':
        return renderDefaultRow(i, checkLabel('Comprometido'), commit, undefined, false, annulled('both'))
      case 'availableBudget':
        return renderDefaultRow(i, checkLabel('Verba disponível'), (item.filteredPhysicals - commit))
      case 'availablePos':
        return renderDefaultRow(i, checkLabel('Disponível Positivo'), item.available?.positive)
      case 'availableNeg':
        return renderDefaultRow(i, checkLabel('Disponível Negativo'), item.available?.negative)
      case 'extraValues':
        return renderDefaultRow(i, checkLabel('Estouros / Economias'), item.extraValue?.reduce((a, b) => a + b.value, 0))
      // Em CUB ≡ Orçado (÷ base) — coluna visível, nunca divergente (decisão Leo 30/07)
      case 'indexedBudget':
        return renderDefaultRow(i, checkLabel('Orçamento Indexado'), itemIndexedBudgetDisplay(item, activeProject?.indexedBudgetMethod, cubBased), undefined, !cubBased)
      case 'availableBudgetIndexed':
        return renderDefaultRow(i, checkLabel('Verba disponível (Indexada)'), (itemIndexedBudgetDisplay(item, activeProject?.indexedBudgetMethod, cubBased) - commit), undefined, !cubBased)

      default:
        break;
    }
  }

  return (

    <div className="item" style={{ boxSizing: 'border-box', minWidth: '100%', width: 'max-content' }}>
      <div className="accordion-item-body" style={getStyles()}>
        <div style={{ width: '450px' }}>
          <div className="flex align-center gap-8">
            {ChildAccordionComponent && (
              <button
                className={`accordion-toggle ${toggle ? "active" : ""}`}
                onClick={() => setToggle(!toggle)}
              ></button>
            )}
            {type === 'totals' ? <span className="accordion-title">Valores Totais</span> : <div className="grid">
              <span className="accordion-label">EAP #{item.building_unit_id}{item.wbs ? `.${item.wbs}` : ""} / {typeTitle}</span>
              <span className="accordion-title">{item.description ?? item.building_unit_name}</span>
            </div>}

          </div>
        </div>
        <div className="column-span-2-tablet">
          <div className="flex wrap-wrap wrap-nowrap-tablet gap-16 align-center">
            <div className={`accordion-content`} style={{ display: 'flex', width: 'auto' }}>
              {visibleColumns.map(i => (
                <Fragment key={i}>{checkCard(i)}</Fragment>
              ))}
            </div>
            <div className="accordion-actions" style={{ paddingLeft: '20px', paddingRight: '40px' }}>
              <button className="toggle-offcanvas" onClick={onShowDetail}>
                <i
                  className="icon icon-width-16 icon-ellipsis-h"
                  aria-hidden={true}
                ></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      {toggle && <ChildAccordionComponent parent={item.wbs_code_id} />}
    </div>
  )
}

export default AccordionItem;
